import { useState } from 'react';
import { useStore } from '@/lib/store';
import { enrich } from '@/lib/api';

const industries = [
  'Technology',
  'E-commerce',
  'Healthcare',
  'Finance',
  'Real Estate',
  'Education',
  'Food & Beverage',
  'Travel',
  'Fitness',
  'Consulting',
  'Marketing',
  'Legal',
  'Other',
];

export function StepOne() {
  const { formData, setFormData } = useStore();
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  const [enrichSuccess, setEnrichSuccess] = useState(false);

  const handleAutoFill = async () => {
    if (!formData.websiteUrl) {
      setEnrichError('Please enter a website URL first');
      return;
    }

    setIsEnriching(true);
    setEnrichError(null);
    setEnrichSuccess(false);

    try {
      const response = await enrich.fromWebsite(formData.websiteUrl);

      if (response.success && response.data) {
        const data = response.data;

        // Update form with enriched data
        setFormData({
          businessName: data.businessName || formData.businessName,
          description: data.description || data.tagline || formData.description,
          logoUrl: data.logo || formData.logoUrl,
          tagline: data.tagline || formData.tagline,
          primaryColor: data.themeColor || formData.primaryColor,
        });

        // Also update hero section details with tagline
        if (data.tagline) {
          setFormData({
            sectionDetails: {
              ...formData.sectionDetails,
              hero: {
                ...formData.sectionDetails?.hero,
                subheadline: data.tagline,
              },
            },
          });
        }

        setEnrichSuccess(true);
        setTimeout(() => setEnrichSuccess(false), 3000);
      }
    } catch (error) {
      setEnrichError('Could not fetch website data. Please fill in the fields manually.');
    } finally {
      setIsEnriching(false);
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Tell us about your business</h2>
        <p className="text-gray-600">We'll use this to create a landing page that fits your brand.</p>
      </div>

      {/* Website URL with Auto-fill */}
      <div className="bg-gradient-to-r from-primary-50 to-accent-50 p-5 rounded-xl border-2 border-primary-100">
        <label htmlFor="websiteUrl" className="label flex items-center gap-2">
          <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          Have an existing website? (Optional)
        </label>
        <div className="flex gap-3 mt-2">
          <input
            type="url"
            id="websiteUrl"
            className="input flex-1"
            placeholder="https://yourbusiness.com"
            value={formData.websiteUrl || ''}
            onChange={(e) => {
              setFormData({ websiteUrl: e.target.value });
              setEnrichError(null);
            }}
          />
          <button
            type="button"
            onClick={handleAutoFill}
            disabled={isEnriching || !formData.websiteUrl}
            className="btn-primary px-4 py-2 whitespace-nowrap disabled:opacity-50"
          >
            {isEnriching ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Fetching...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Auto-fill
              </>
            )}
          </button>
        </div>
        {enrichError && (
          <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {enrichError}
          </p>
        )}
        {enrichSuccess && (
          <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Fields updated from your website!
          </p>
        )}
        <p className="text-xs text-gray-500 mt-2">
          We'll pull your logo, tagline, and brand colors to pre-fill the form.
        </p>
      </div>

      {/* Logo Preview (if available) */}
      {formData.logoUrl && (
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <img
            src={formData.logoUrl}
            alt="Logo"
            className="w-16 h-16 object-contain rounded-lg bg-white p-2 border"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <div>
            <p className="text-sm font-medium text-gray-700">Logo detected</p>
            <p className="text-xs text-gray-500">This will be used in your landing page</p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="businessName" className="label">
            Business Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="businessName"
            className="input"
            placeholder="Acme Inc."
            value={formData.businessName || ''}
            onChange={(e) => setFormData({ businessName: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="industry" className="label">
            Industry <span className="text-red-500">*</span>
          </label>
          <select
            id="industry"
            className="input"
            value={formData.industry || ''}
            onChange={(e) => setFormData({ industry: e.target.value })}
          >
            <option value="">Select an industry</option>
            {industries.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="description" className="label">
            Business Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            className="input min-h-[120px]"
            placeholder="Describe what your business does, your products or services, and what makes you unique..."
            value={formData.description || ''}
            onChange={(e) => setFormData({ description: e.target.value })}
          />
          <p className="text-sm text-gray-500 mt-1">
            {(formData.description?.length || 0)} / 2000 characters
          </p>
        </div>

        <div>
          <label htmlFor="targetAudience" className="label">
            Target Audience
          </label>
          <textarea
            id="targetAudience"
            className="input min-h-[80px]"
            placeholder="Who is your ideal customer? (e.g., 'Small business owners aged 30-50 looking to grow their online presence')"
            value={formData.targetAudience || ''}
            onChange={(e) => setFormData({ targetAudience: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}
